import React, { memo } from 'react';

// 🔴 Фигуры и цвета импортируются у `TrackIcon`, а не рисуются заново: профессия
// и группа обязаны выглядеть одинаково на холсте, в списке и в каталоге
// профессий. Две копии одних и тех же путей разъезжаются на первой же правке.
import { COLORS, SHAPES } from 'atoms/TrackIcon/TrackIcon';

/**
 * Знак профессии или группы ВНУТРИ холста (ТЗ 0070).
 *
 * Не `<TrackIcon>` по той же причине, по которой знак навыка рисует
 * `SkillGlyph`, а не `SkillIcon`: вложить HTML в SVG можно только через
 * `foreignObject`, а он ломает и масштабирование сцены, и печать страницы.
 *
 * `disc` — подложка-кружок, как у `TrackIcon` на карточках профессий. У группы
 * её нет: там знак стоит внутри кольца прогресса, и второй круг под ним
 * читался бы как ещё один индикатор.
 *
 * ⚠️ Прозрачность подложки задаётся `fillOpacity`, а не восьмизначным hex
 * (`${color}1F`, как в CSS у `TrackIcon`): в SVG-атрибуте это другой путь
 * разбора, и явное число однозначно.
 */
interface ITrackGlyph {
  x: number;
  y: number;
  size: number;
  /** Ключ фигуры. Незнакомый и пустой — портфель, как у `TrackIcon`. */
  icon: string;
  disc?: boolean;
}

const FALLBACK = 'briefcase';

export const TrackGlyph = memo(({ x, y, size, icon, disc = false }: ITrackGlyph) => {
  const key = icon && SHAPES[icon] ? icon : FALLBACK;
  const color = COLORS[key];
  // Фигуры нарисованы в координатах 24×24 — приводим к запрошенной стороне.
  const inset = disc ? size * 0.2 : 0;
  const scale = (size - inset * 2) / 24;

  return (
    <g transform={`translate(${x} ${y})`} aria-hidden focusable="false">
      {disc && <circle cx={size / 2} cy={size / 2} r={size / 2} fill={color} fillOpacity={0.12} />}
      <g
        transform={`translate(${inset} ${inset}) scale(${scale})`}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round">
        {SHAPES[key].map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    </g>
  );
});
